/* ==========================================================================
   Segal — Name Necklace UI  (the "Generate necklace" block)
   --------------------------------------------------------------------------
   WHY THIS FILE EXISTS
   Until now the only way a customer could start a generation was to type into
   the theme's own "Name necklace" field, and the switcher fired on every
   keystroke burst. That is invisible (nothing on the page says we will make
   any name) and it is expensive: a >1.5s pause mid-typing built and published
   `gold-name-necklace-silv` 22 seconds before `silvia`. Both products were
   paid for; one is junk.

   This file replaces that field with an explicit block:
       1 · Default   — name input + "Generate necklace"
       2 · Making it — locked input, progress bar, Add to cart DISABLED
       3 · Ready     — confirmation, button becomes "Change name"

   It does NOT reimplement the switcher. It hides the raw field, and when the
   button is pressed it writes the COMPLETE name into that same field and
   fires one `input` + one `change` event. The switcher runs exactly the flow
   it always ran — just once, and never on a prefix.

   It also repairs the copy and the images below the fold, which the switcher
   never touched.

   Load order: switcher-v3 -> guard -> this file.
   ========================================================================== */
(() => {
  'use strict';

  const INK    = '#354e57';   // theme dark teal (matches Add to cart)
  const GOLD   = '#b78b43';   // theme gold (same value the switcher's glint uses)
  const MAX_WAIT_MS = 150000;   // 3 QC attempts take ~110-130s; the switcher's own message wins before this
  const TICK_MS     = 300;

  const cfg = window.SegalNameNecklace || {};
  const SELECTORS = cfg.inputSelectors || [
    '[data-name-necklace-input]',
    'input[name="properties[Custom Name]"]',
    'input[name="properties[Name]"]'
  ];

  const slug = (s) => {
    const t = String(s || '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (t) return t;
    // A Hebrew/Arabic/Chinese... name has no ASCII slug. The Worker names those products by a
    // stable hash of the name (hebrew-gold-name-necklace-<hash>); name-necklace-plate.js has the
    // same hash, so the URL test below still fires exactly once, at the end.
    const raw = String(s || '').trim().replace(/\s+/g, ' ');
    return raw && window.SegalPlate ? window.SegalPlate.hash(raw) : '';
  };

  const findInput = () => {
    for (const sel of SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  };

  const atcButtons = () => [...document.querySelectorAll(
    'button[name="add"], .js-product-button-add-to-cart, [data-js-product-add-to-cart]'
  )];

  /* ---------------------------------------------------------------- styles */
  const CSS = `
.snui{border:1px solid rgba(112,112,112,.25);border-left:3px solid ${GOLD};
  background:#fcfaf5;padding:14px 15px 15px;margin:0 0 18px}
.snui *{box-sizing:border-box}
.snui__t{font-size:15px;line-height:1.3;color:${INK};margin:0 0 3px}
.snui__s{font-size:13px;line-height:1.5;color:rgba(53,78,87,.72);margin:0 0 11px}
.snui__row{display:flex;gap:8px;align-items:stretch}
.snui__in{flex:1;min-width:0;height:42px;border:1px solid rgba(112,112,112,.35);background:#fff;
  color:${INK};font-family:inherit;font-size:14px;padding:0 12px;border-radius:0;-webkit-appearance:none}
.snui__in:focus{outline:none;border-color:${GOLD}}
.snui__in[disabled]{background:#f4f2ec;color:rgba(53,78,87,.6)}
.snui__go{flex:none;height:42px;padding:0 18px;border:1px solid ${INK};background:${INK};color:#fff;
  font-family:inherit;font-size:13px;letter-spacing:.65px;text-transform:uppercase;cursor:pointer;
  border-radius:0;white-space:nowrap;transition:opacity .15s}
.snui__go:hover{opacity:.88}
.snui__go[disabled]{background:#9fb2bb;border-color:#9fb2bb;cursor:default;opacity:1}
.snui__note{font-size:12px;line-height:1.4;color:rgba(53,78,87,.6);margin-top:9px;
  display:flex;align-items:center;gap:7px}
.snui__dot{width:7px;height:7px;border-radius:50%;background:#3f9b6d;flex:none}
.snui__dot--amber{background:${GOLD}}
.snui__bar{height:4px;background:#ece5d6;overflow:hidden;margin-top:11px}
.snui__bar > i{display:block;height:100%;width:0;background:${GOLD};transition:width .4s linear}
.snui__ok{font-size:13px;color:#2f6f4a;margin-top:9px}
.snui__err{font-size:13px;color:#a4342b;margin-top:9px}
.snui__err .snui__msg{color:${INK};text-decoration:underline;margin-left:4px;font-weight:600}
.snui--busy .snui__in{pointer-events:none}
.selector-wrapper.name.snui-hidden{display:none !important}
@media (max-width:480px){
  .snui__row{flex-wrap:wrap}
  .snui__go{width:100%}
}
`;

  const injectCss = () => {
    if (document.getElementById('snui-css')) return;
    const s = document.createElement('style');
    s.id = 'snui-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  };

  /* ------------------------------------------------------------------ view */
  const build = () => {
    const box = document.createElement('div');
    box.className = 'snui';
    box.innerHTML = `
      <div class="snui__t"></div>
      <div class="snui__s"></div>
      <div class="snui__row">
        <input class="snui__in" type="text" autocomplete="off" spellcheck="false">
        <button class="snui__go" type="button"></button>
      </div>
      <div class="snui__bar" hidden><i></i></div>
      <div class="snui__note"><span class="snui__dot"></span><span class="snui__noteText"></span></div>
      <div class="snui__ok" hidden></div>
      <div class="snui__err" hidden></div>`;
    return box;
  };

  const start = () => {
    const raw = findInput();
    if (!raw) return;

    const host = document.querySelector('.product-page-info__variants-inner')
              || raw.closest('.product-page-info__variants')
              || raw.parentElement.parentElement;
    if (!host || host.querySelector('.snui')) return;

    injectCss();

    const box = build();
    host.insertBefore(box, host.firstChild);

    // hide the raw field but KEEP it in the form — it still carries
    // properties[Custom Name] into the cart, and the switcher still binds it.
    const wrap = raw.closest('.selector-wrapper');
    if (wrap) wrap.classList.add('snui-hidden');

    const $t    = box.querySelector('.snui__t');
    const $s    = box.querySelector('.snui__s');
    const $in   = box.querySelector('.snui__in');
    const $go   = box.querySelector('.snui__go');
    const $bar  = box.querySelector('.snui__bar');
    const $fill = box.querySelector('.snui__bar > i');
    const $note = box.querySelector('.snui__note');
    const $dot  = box.querySelector('.snui__dot');
    const $noteT= box.querySelector('.snui__noteText');
    const $ok   = box.querySelector('.snui__ok');
    const $err  = box.querySelector('.snui__err');

    // ------------------------------------------------------------------ dirty
    // 2026-09-02, reported by Yaniv: "when writing a name, clicking enter should be equal to
    // generate button". Enter was already wired - the real fault was that the customer's typed
    // name was being wiped out from under them before Enter could act on it.
    //
    // render('idle') used to reset this field to the product's own name unconditionally, and
    // it runs on window 'load' (which on a product page fires only after all twelve gallery
    // images finish - several seconds in), again on a 1500ms timer, and again on EVERY
    // metal-selector change event. Measured live on diamond-gold-name-necklace-vanessa: focus
    // the field, select all, type "Nicole", and a second later it reads "Vanessa" again.
    // Enter then submitted "Vanessa" - the name already on screen - which the handler
    // correctly treats as a no-op. So Enter looked broken, and so did the button.
    //
    // Once the customer has typed in this field, the field belongs to them.
    let dirty = false;
    $in.addEventListener('input', () => { dirty = true; });

    // ------------------------------------------------------ unsupported alphabets
    // 2026-09-04, Yaniv: "block the option to type languages we are not supporting, so the
    // client will not have a bad experience". The generator makes Latin-letter names only
    // (Hebrew, Arabic, Chinese... are coming, one font at a time). Before this, a customer
    // typing עדן pressed Generate, waited for the round trip and got a rejection. Now the
    // block appears as they type, Generate is disabled, Enter does nothing, and a
    // "Message us" link opens the same contact popup the team already answers.
    const LATIN_OK = /^[ -~ -ɏḀ-ỿ’]*$/;
    // 2026-09-04: plate mode. Gold names may be typed in English (and other Latin-alphabet
    // languages), Hebrew, Arabic, Russian, Chinese, Japanese, Korean, Hindi, Thai or Armenian -
    // one alphabet per name; name-necklace-plate.js holds the list and draws the piece. Diamond
    // names stay Latin-only until the pavé flow is moved to plates too.
    const UNSUPPORTED = cfg.family === 'diamond'
      ? 'Diamond names are made in English letters for now. For other alphabets, message us and we’ll cut it by hand. '
      : 'We make names in English, Hebrew, Arabic, Russian, Chinese, Japanese, Korean, Hindi, Thai '
      + 'and Armenian letters - one alphabet per name. For anything else, message us and we’ll cut it by hand. ';
    const scriptSupported = (v) => {
      const s = String(v || '');
      if (!s.trim()) return true;
      if (cfg.family === 'diamond' || !window.SegalPlate) return LATIN_OK.test(s);
      return window.SegalPlate.supported(s);
    };
    const showUnsupported = () => {
      $err.hidden = false;
      $err.textContent = UNSUPPORTED;
      const a = document.createElement('a');
      a.href = '#'; a.className = 'snui__msg'; a.textContent = 'Message us';
      $err.appendChild(a);
      $go.disabled = true;
    };
    const clearUnsupported = () => {
      if (!$err.querySelector('.snui__msg')) return;
      $err.hidden = true; $err.textContent = '';
      if (state !== 'busy') $go.disabled = false;
    };
    $in.addEventListener('input', () => { if (scriptSupported($in.value)) clearUnsupported(); else showUnsupported(); });

    // Opens the theme's own customize popup (same form the team already receives).
    const CONTACT_TRIGGER = '[data-js-popup-button="customize-product"]';
    const CONTACT_POPUP = '.popup-customize-product';
    let contactIntro = null;
    const openContact = (name) => {
      const trigger = document.querySelector(CONTACT_TRIGGER);
      const popup = document.querySelector(CONTACT_POPUP);
      if (!trigger || !popup) return false;
      const intro = popup.querySelector('p.text-center');
      if (intro && contactIntro === null) contactIntro = intro.textContent;
      if (intro) intro.textContent = 'Tell us the name and the alphabet you would like — we make these by hand.';
      const ta = popup.querySelector('textarea[name="contact[message]"]');
      if (ta && !String(ta.value || '').trim() && name) ta.value = 'The name I would like: ' + name + '\n\n';
      if (!document.body.classList.contains('popup-opened')) trigger.click();
      const restore = setInterval(() => {
        if (document.body.classList.contains('popup-opened')) return;
        if (intro && contactIntro !== null) intro.textContent = contactIntro;
        clearInterval(restore);
      }, 400);
      return true;
    };
    box.addEventListener('click', (e) => {
      const a = e.target && e.target.closest ? e.target.closest('.snui__msg') : null;
      if (!a) return;
      e.preventDefault();
      openContact(String($in.value || '').trim());
    });

    const currentMetal = () => {
      const sel = document.querySelector('.single-option-selector[data-option="option1"]');
      if (sel && sel.value) return sel.value;
      const opener = document.querySelector('.custom-select-opener span');
      if (opener && opener.textContent.trim()) return opener.textContent.trim();
      const chosen = document.querySelector('.product-selected-options');
      return (chosen && chosen.textContent.trim()) || '';
    };
    const currentName = () => String(raw.value || '').trim();

    let barTimer = null;
    const stopBar = () => { if (barTimer) { clearInterval(barTimer); barTimer = null; } };
    const runBar = () => {
      stopBar();
      let pct = 4;
      $fill.style.width = pct + '%';
      barTimer = setInterval(() => {
        pct += pct < 88 ? 2.6 : 0.25;      // ~30s to 88%, then a slow crawl
        $fill.style.width = Math.min(pct, 97) + '%';
      }, 900);
    };

    // The switcher prints its own "working on the design" line into the info
    // column. Our block now says the same thing, with the measured duration, so
    // without this two different countdowns would sit on screen at once.
    const hiddenStatus = [];
    const hideDuplicateStatus = () => {
      const scope = document.querySelector('.product-page-info__variants') || document.body;
      [...scope.querySelectorAll('*')].forEach((el) => {
        if (el.closest('.snui') || el.children.length) return;
        if (!/working on the design/i.test(el.textContent || '')) return;
        if (el.style.display === 'none') return;
        hiddenStatus.push([el, el.style.display]);
        el.style.display = 'none';
      });
    };
    const restoreDuplicateStatus = () => {
      while (hiddenStatus.length) {
        const pair = hiddenStatus.pop();
        pair[0].style.display = pair[1] || '';
      }
    };

    /* ------------------------------------------------- stale copy below fold */
    // The switcher swaps the H1, the gallery, the price and the cart variant,
    // but nothing below the fold: the breadcrumb, both description blocks, the
    // custom-request panel, five popup headings and every image alt still named
    // the previous necklace. Measured on one swap: 22 stale places, 24 text
    // nodes to repair.
    const TITLE_SEL = [
      '.product-custom-request .product-title',
      '.popup-rush-my-orders .product-title',
      '.popup-customize-product h4',
      '.popup-more-details-product h4',
      '.popup-no-product-found h4',
      'div.container > ul.list-unstyled.d-flex > li > span'
    ].join(', ');

    const COPY_SCOPES = [
      '.item-specification',
      '.tabs__content',
      '.product-custom-request',
      '.popup-rush-my-orders',
      '.popup-customize-product',
      '.popup-more-details-product',
      '.popup-no-product-found',
      '.product-gallery__main_item',
      '.product-gallery__thumbnail_item',
      '.product-page-info__button-add-to-cart'
    ].join(', ');

    // Titles come in two shapes on this store, and the older hand-built names
    // use the second: "Gold Name Necklace – Ophira | 14K Solid Gold" and plain
    // "Gold Name Necklace - Lia". Splitting on the en-dash alone returned '' for
    // Lia, which made the whole sync a no-op for every legacy name.
    const esc = (v) => String(v).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const hasName = (text, nm) =>
      new RegExp('(^|[^A-Za-z0-9])' + esc(nm) + '(?![A-Za-z0-9])', 'i').test(String(text || ''));

    const nameOf = (t) => String(t || '')
      .split('|')[0]
      .replace(/^\s*\d*\s*k?\s*(yellow|white|rose)?\s*gold\s*name\s*necklace\s*/i, '')
      .replace(/^[\s–—-]+/, '')
      .trim();

    const syncCopy = (wantedName) => {
      const h1 = document.querySelector('.product-page-info__title h1');
      const newTitle = h1 ? h1.textContent.trim() : '';
      // the name the customer asked for is authoritative; the H1 is the fallback
      const newName = String(wantedName || nameOf(newTitle)).trim();
      if (!newName) return 0;

      let staleTitle = '';
      for (const el of document.querySelectorAll(TITLE_SEL)) {
        const t = (el.textContent || '').trim();
        if (!t || !/name necklace/i.test(t)) continue;
        if (hasName(t, newName)) continue;
        if (!nameOf(t)) continue;
        staleTitle = t;
        break;
      }
      const staleName = nameOf(staleTitle);
      if (!staleName || staleName.toLowerCase() === newName.toLowerCase()) return 0;

      const re = new RegExp('(^|[^A-Za-z0-9])' + esc(staleName) + '(?![A-Za-z0-9])', 'g');
      const swap = (str) => String(str).replace(re, (m, pre) => pre + newName);

      let changed = 0;

      // the guard rewrites document.title too, but it splits on the en-dash, so
      // it leaves legacy titles ("Gold Name Necklace - Lia") stale in the tab.
      if (hasName(document.title, staleName)) {
        document.title = swap(document.title);
        changed += 1;
      }

      // elements whose whole text IS the old title get the real new title, not a
      // token swap - the two products can spell their titles differently.
      document.querySelectorAll(TITLE_SEL).forEach((el) => {
        if (el.closest('.product-recently-viewed')) return;
        if ((el.textContent || '').trim() !== staleTitle) return;
        el.textContent = newTitle || swap(staleTitle);
        changed += 1;
      });

      [...document.querySelectorAll(COPY_SCOPES)].forEach((root) => {
        // "Recently viewed" legitimately lists other people's necklaces - those
        // names are not stale and must never be rewritten.
        if (root.closest('.product-recently-viewed')) return;
        const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walk.nextNode())) {
          const before = node.nodeValue || '';
          const after = swap(before);
          if (after !== before) { node.nodeValue = after; changed += 1; }
        }
        root.querySelectorAll('img[alt]').forEach((img) => {
          const before = img.getAttribute('alt') || '';
          const after = swap(before);
          if (after !== before) { img.setAttribute('alt', after); changed += 1; }
        });
      });

      if (changed) {
        const M = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
        M.lastCopySync = { from: staleName, to: newName, nodes: changed, at: new Date().toISOString() };
      }
      return changed;
    };

    // The description tab and the item-specification block each carry their own
    // copy of the product photo, and the switcher repaints only the gallery. On
    // a swap they were left showing the previous necklace beside the new name.
    const stem = (url) => String(url || '')
      .split('?')[0].split('/').pop()
      .replace(/\.(jpg|jpeg|png|webp|gif|avif)$/i, '')
      .replace(/\.progressive$/i, '')
      .replace(/_\{width\}x$/i, '')
      .replace(/_\d+x\d*$/i, '')
      .toLowerCase();

    // scoped to the theme's .rimage product-image component so that static
    // furniture ("your-order-includes.png", review avatars) is never touched
    const DESC_IMG = '.tabs__content .rimage img.rimage__img, '
                   + '.item-specification .rimage img.rimage__img';

    let mediaBusy = false;
    const syncMedia = async () => {
      if (mediaBusy) return 0;
      const handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      const nodes = [...document.querySelectorAll(DESC_IMG)];
      if (!handle || !nodes.length) return 0;
      mediaBusy = true;
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js',
          { credentials: 'same-origin', cache: 'no-store' });
        if (!res.ok) return 0;
        const product = await res.json();
        const feat = product.featured_image || (product.images || [])[0];
        if (!feat) return 0;
        const own = new Set((product.images || []).map(stem));
        let n = 0;
        nodes.forEach((img) => {
          const src = img.getAttribute('data-master') || img.currentSrc || img.getAttribute('src') || '';
          if (!src || /^data:/i.test(src)) return;
          if (own.has(stem(src))) return;
          img.removeAttribute('data-master');
          img.classList.remove('lazyload', 'lazyload--hold');
          img.src = feat;
          img.srcset = feat;
          n += 1;
        });
        return n;
      } catch (_) { return 0; }
      finally { mediaBusy = false; }
    };

    // the theme writes some of these blocks late, so sweep for a while after
    const sweepCopy = (nm) => [0, 400, 1000, 2000, 4000, 7000]
      .forEach((d) => setTimeout(() => { syncCopy(nm); syncMedia(); }, d));

    const setAtc = (enabled) => atcButtons().forEach((b) => {
      if (enabled) { b.removeAttribute('disabled'); b.style.removeProperty('opacity'); }
      else { b.setAttribute('disabled', 'disabled'); b.style.opacity = '.55'; }
    });

    let state = '';
    const render = (next, data) => {
      state = next;
      box.classList.toggle('snui--busy', next === 'busy');
      $ok.hidden = true; $err.hidden = true; $bar.hidden = true; $note.hidden = false;

      if (next === 'idle') {
        stopBar(); setAtc(true); restoreDuplicateStatus();
        // There is no blank "Gold Name Necklace" product - every page a customer
        // can land on already carries a name. So this, not the empty variant, is
        // the state almost everyone sees, and it is the only thing on the page
        // that tells them we will make any name at all.
        $t.textContent = 'Make it yours — any name';
        $s.textContent = data
          ? 'This one says “' + data + '”. Type any name, in any language, and we will '
            + 'show it to you — or make it, in about 30 seconds.'
          : "Type any name in any language. If we already make it you'll see it "
            + "instantly; if not we'll create it for you.";
        $in.disabled = false;
        $in.placeholder = 'Type a name, e.g. Ophira';
        // never overwrite a name the customer is in the middle of typing
        if (data && !dirty && document.activeElement !== $in) $in.value = data;
        $go.disabled = false;
        $go.textContent = 'Generate necklace';
        $dot.className = 'snui__dot';
        $noteT.textContent = 'Over 600 names ready now · new names take about 30 seconds';
        if (!scriptSupported($in.value)) showUnsupported();
        return;
      }

      if (next === 'busy') {
        setAtc(false); runBar();
        const metal = currentMetal();
        $t.textContent = 'Making your “' + data + '” necklace';
        $s.textContent = "We're cutting the letters and setting the chain. "
                       + 'You can watch it appear on the left.';
        dirty = false;
        $in.value = data;
        $in.disabled = true;
        $go.disabled = true;
        $go.textContent = 'Working…';
        $bar.hidden = false;
        $dot.className = 'snui__dot snui__dot--amber';
        $noteT.textContent = 'About 30 seconds' + (metal ? ' · in ' + metal : '');
        return;
      }

      if (next === 'ready') {
        stopBar(); setAtc(true);
        const metal = currentMetal();
        $t.textContent = 'Your “' + data + '” necklace is ready';
        $s.textContent = metal
          ? 'Shown in ' + metal + '. Change the metal below and the photo updates.'
          : 'Change the metal below and the photo updates.';
        dirty = false;
        $in.value = data;
        $in.disabled = false;
        $go.disabled = false;
        $go.textContent = 'Change name';
        $note.hidden = true;
        $ok.hidden = false;
        $ok.textContent = '✓ Made for you · available in every metal';
        return;
      }

      if (next === 'failed') {
        stopBar(); setAtc(true); restoreDuplicateStatus();
        $t.textContent = "We couldn't make that one";
        $s.textContent = 'Try again, or pick a different spelling. Nothing has been charged.';
        $in.disabled = false;
        $go.disabled = false;
        $go.textContent = 'Try again';
        $note.hidden = true;
        $err.hidden = false;
        $err.textContent = 'If it keeps failing, message us and we will cut it by hand.';
      }
    };

    /* ------------------------------------------------------------- trigger */
    const nativeValue = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value'
    ).set;

    let watching = false;
    const generate = (wanted) => {
      if (watching) return;
      const previousName = currentName();
      render('busy', wanted);

      // the theme locks this field until "Edit Name" is pressed; that button is
      // gone now, so unlock it ourselves immediately before writing.
      raw.disabled = false;
      raw.readOnly = false;
      raw.classList.remove('disabled');

      nativeValue.call(raw, wanted);
      raw.dispatchEvent(new Event('input',  { bubbles: true }));
      raw.dispatchEvent(new Event('change', { bubbles: true }));

      watching = true;
      const t0 = Date.now();
      const target = slug(wanted);
      let calm = 0;        // consecutive quiet ticks

      const timer = setInterval(() => {
        const elapsed = Date.now() - t0;

        // The overlay is NOT removed when a render finishes - it is left in the
        // DOM with display:none, so presence is not a busy signal. offsetParent
        // is not one either (it reads null for a fixed/transformed ancestor, and
        // that is what flipped this block to "ready" 3.9s into a 28.9s build).
        // getClientRects() is the honest test: empty iff the element paints
        // nothing.
        const drawing = [...document.querySelectorAll('.segal-name-drawing')]
          .some((el) => el.getClientRects().length > 0);
        const busy = drawing || !!document.querySelector('[data-sn-busy]');
        if (busy) { calm = 0; } else { calm += 1; }

        // the theme replaces the Add-to-cart node on a product swap, which
        // silently restores the button we disabled - so re-assert it each tick.
        setAtc(false);
        hideDuplicateStatus();
        syncCopy(wanted);

        // The URL landing on the finished product is the only signal that is
        // true exactly once, at the end. The raw field carries the typed name
        // immediately, so it can never be the primary test.
        const pathMatches = !!target && slug(location.pathname).endsWith(target);
        // The switcher records the moment it finished for THIS name; a hand-built product
        // reached by an index hit has a handle that no slug of the typed name matches.
        const lr = (window.SegalNameNecklaceMetrics || {}).lastReady;
        const readyMatches = !!(lr && lr.name && slug(lr.name) === target && Date.parse(lr.measuredAt || 0) >= t0 - 1000);

        // The switcher reports a refusal (rate limit, rejected name, review) in its own status
        // line the moment it happens. Measured 2026-09-04: this block kept saying "Making your
        // necklace" for the full 120s while that line already read "maximum number of name
        // necklaces". Surface it here at once, in the switcher's words, and unlock the field.
        // 2026-09-05 (mobile QA, Arabic ليلى): this test MUST run before the ready test - the
        // job ended in "needs a jewelry review" at 70s, and the old order let the 45s fallback
        // below flip the block to "ready" on the previous product's photo two seconds later.
        const said = document.querySelector('.segal-name-message');
        const refusal = said && !said.hidden && (said.dataset.snError === 'true'
          || (said.getClientRects().length && /maximum|couldn|contact us|review|try again|not ready|message us|not available|too long/i.test(said.textContent || '')));
        if (refusal && !busy && elapsed > 2500) {
          clearInterval(timer); watching = false;
          restoreDuplicateStatus();
          // A refused name has no matching product. Restore the form's actual name
          // before re-enabling checkout; never sell the old variant with the refused name.
          const message = said.textContent.trim();
          nativeValue.call(raw, previousName);
          dirty = false;
          render('idle', previousName);
          sweepCopy(previousName);
          $err.hidden = false; $err.textContent = message;
          return;
        }

        // Ready needs evidence: the URL of the finished product (the switcher navigates there)
        // or the switcher's own lastReady stamp for this name. The old "busy ended and the
        // field still holds the name after 45s" fallback was for the in-place swap and is gone.
        const done = (pathMatches || readyMatches) && !busy && calm >= 3 && elapsed > 1200;
        if (done) {
          clearInterval(timer); watching = false;
          restoreDuplicateStatus();
          render('ready', wanted);
          sweepCopy(wanted);
          return;
        }
        if (elapsed > MAX_WAIT_MS) {
          clearInterval(timer); watching = false;
          restoreDuplicateStatus();
          render('failed', wanted);
        }
      }, TICK_MS);
    };

    const submit = () => {
      const wanted = String($in.value || '').trim().replace(/\s+/g, ' ');
      const same = !wanted || slug(wanted) === slug(currentName());
      // "Change name" pressed with nothing new typed: unlock the field for editing.
      // Measured 2026-09-02: after a swap the block sits in 'ready', and the OLD rule sent
      // every Enter here regardless of what had been typed - so a customer who swapped to
      // Charlotte, typed Yardena and hit Enter got their field reset instead of a necklace,
      // and had to press Enter a second time. A new name in the field is a request, whatever
      // state the block is in.
      if (state === 'ready' && same) {
        dirty = false;
        render('idle', currentName());
        $in.focus(); $in.select();
        return;
      }
      if (!wanted) { $in.focus(); return; }
      if (!scriptSupported(wanted)) { showUnsupported(); $in.focus(); return; }
      // asking for the name already on screen is a no-op, not a generation
      if (same) { $in.focus(); $in.select(); return; }
      generate(wanted);
    };

    $go.addEventListener('click', submit);

    // Enter is the button. It calls submit() directly rather than $go.click() so it still
    // works if the button is mid-repaint, and it stops propagation because this field sits
    // inside the theme's product <form> - an un-prevented Enter there submits the form and
    // reloads the page.
    $in.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.keyCode !== 13) return;
      e.preventDefault();
      e.stopPropagation();
      submit();
    });

    // the metal selector can change under us; keep the copy honest
    document.addEventListener('change', (e) => {
      if (!e.target || !e.target.matches || !e.target.matches('.single-option-selector')) return;
      if (state === 'ready') render('ready', currentName());
      if (state === 'idle')  render('idle', currentName());
    }, true);
    // The theme's custom dropdown sets the native select and fires the change through jQuery's
    // trigger(), which native listeners never see - measured 2026-09-04 on Jeanette: metal on
    // White, this block still said "Shown in 14K Yellow Gold". Poll the label instead of trusting
    // the event.
    let shownMetal = currentMetal();
    setInterval(() => {
      const m = currentMetal();
      if (m === shownMetal) return;
      shownMetal = m;
      if (state === 'ready') render('ready', currentName());
      if (state === 'idle')  render('idle', currentName());
    }, 700);

    // 2026-09-05: the switcher no longer swaps the product into the page - it navigates to
    // the real product page and marks the arrival with `?sn=ready`. That is the one case
    // where the page should open on "Your necklace is ready" rather than the browse copy.
    // The marker is removed from the address bar so a reload or a shared link is clean.
    let arrivedReady = false;
    try {
      const here = new URL(location.href);
      if (here.searchParams.get('sn') === 'ready') {
        arrivedReady = true;
        here.searchParams.delete('sn');
        history.replaceState({}, '', here.toString());
      }
    } catch (_) { /* leave the URL alone */ }
    const refresh = () => {
      if (state === 'busy' || watching) return;
      // on first load we show the browse copy, never the "your necklace is
      // ready" copy - nobody asked for the name that happened to be in the URL -
      // unless the switcher just brought us here for exactly that name.
      render(arrivedReady ? 'ready' : 'idle', currentName());
    };
    refresh();
    window.addEventListener('load', refresh, { once: true });
    setTimeout(refresh, 1500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
